import { useState } from 'react'
import { Avatar } from '@/components/ui/shadcn/avatar'
import { type PostContent as FacebookPostContent } from '@backend/lib/platforms/facebook'
import { AiLabeledImage } from './ai-label-preview-overlay'
import type { AiLabelStyle } from '@backend/lib/platforms/aiDisclosure'

type Props = {
  account: { id: string; displayName: string; imageUrl?: string; subline?: string } | undefined
  postContent: FacebookPostContent
  _onStartAuth: () => void
  /** Draw the visible AI label that will be baked into image media at publish. */
  showAiLabel?: boolean
  /** Appearance of that label, so the preview matches the user's settings. */
  aiLabelStyle?: AiLabelStyle
}

export function WorkspacePreviewFacebook({
  account,
  postContent,
  showAiLabel = false,
  aiLabelStyle,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false)

  const caption = postContent?.caption || ''
  const showShowMore = caption.length > 250
  const displayCaption = !caption
    ? 'Write a caption for your post'
    : isExpanded || !showShowMore
      ? caption
      : caption.substring(0, 250) + '...'

  return (
    <div className="mx-auto flex h-full w-full flex-col gap-3 rounded-lg p-3 pb-0 text-black">
      {/* Profile header */}
      <div className="flex items-center gap-2">
        <ProfileHeader
          imageUrl={account?.imageUrl}
          displayName={account?.displayName || 'Your Page'}
        />
      </div>

      {/* Post text */}
      <div>
        <p className="whitespace-pre-wrap text-xs leading-relaxed text-black">{displayCaption}</p>
        {showShowMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-1 text-xs font-medium text-[#1877F2] hover:underline">
            {isExpanded ? 'See less' : 'See more'}
          </button>
        )}
      </div>

      {/* Preview media */}
      <div className="-mx-3">
        {(() => {
          switch (postContent?.type) {
            case 'text':
            case 'link':
              return null

            case 'image':
              return (
                <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                  <AiLabeledImage
                    src={postContent.url}
                    crossOrigin="anonymous"
                    className="max-h-full max-w-full object-contain"
                    showAiLabel={showAiLabel}
                    aiLabelStyle={aiLabelStyle}
                  />
                </div>
              )

            case 'video':
            case 'reel':
              return (
                <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                  <video
                    src={postContent.url}
                    className="max-h-full max-w-full object-contain"
                    controls
                    autoPlay
                    muted
                    playsInline
                  />
                </div>
              )

            case 'multiPhoto': {
              const images = postContent.urls
              const imageCount = images.length

              if (imageCount === 0) return null

              if (imageCount === 1) {
                return (
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                    <AiLabeledImage
                      src={images[0]}
                      crossOrigin="anonymous"
                      className="max-h-full max-w-full object-contain"
                      showAiLabel={showAiLabel}
                      aiLabelStyle={aiLabelStyle}
                    />
                  </div>
                )
              }

              if (imageCount === 2) {
                return (
                  <div className="flex gap-1">
                    {images.map((url) => (
                      <div
                        key={url}
                        className="flex aspect-square flex-1 items-center justify-center bg-gray-100">
                        <AiLabeledImage
                          src={url}
                          crossOrigin="anonymous"
                          className="max-h-full max-w-full object-contain"
                          showAiLabel={showAiLabel}
                          aiLabelStyle={aiLabelStyle}
                        />
                      </div>
                    ))}
                  </div>
                )
              }

              // 3+ images: first large, then a row of 2 with overflow badge
              const firstImage = images[0]
              const remaining = images.slice(1)
              const visible = remaining.slice(0, 2)
              const hidden = remaining.length - 2

              return (
                <div className="flex flex-col gap-1">
                  <div className="flex aspect-square w-full items-center justify-center bg-gray-100">
                    <AiLabeledImage
                      src={firstImage}
                      crossOrigin="anonymous"
                      className="max-h-full max-w-full object-contain"
                      showAiLabel={showAiLabel}
                      aiLabelStyle={aiLabelStyle}
                    />
                  </div>
                  {visible.length > 0 && (
                    <div className="flex gap-1">
                      {visible.map((url, index) => (
                        <div
                          key={url}
                          className="relative flex aspect-square flex-1 items-center justify-center bg-gray-100">
                          <AiLabeledImage
                            src={url}
                            crossOrigin="anonymous"
                            className="max-h-full max-w-full object-contain"
                            showAiLabel={showAiLabel}
                            aiLabelStyle={aiLabelStyle}
                          />
                          {index === visible.length - 1 && hidden > 0 && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                              <span className="text-2xl font-semibold text-white">+{hidden}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            }

            // Stories render in vertical 9:16 to mimic Facebook's native story
            // viewer. Caption is intentionally not shown — Meta strips it for
            // stories at the API level, so showing it would be misleading.
            case 'photoStory':
              return (
                <div className="mx-auto flex aspect-[9/16] max-w-[280px] items-center justify-center overflow-hidden rounded-lg bg-black">
                  <AiLabeledImage
                    src={postContent.url}
                    crossOrigin="anonymous"
                    className="h-full w-full object-cover"
                    showAiLabel={showAiLabel}
                    aiLabelStyle={aiLabelStyle}
                    wrapperClassName="h-full w-full"
                  />
                </div>
              )

            case 'videoStory':
              return (
                <div className="mx-auto flex aspect-[9/16] max-w-[280px] items-center justify-center overflow-hidden rounded-lg bg-black">
                  <video
                    src={postContent.url}
                    className="h-full w-full object-cover"
                    controls
                    autoPlay
                    muted
                    playsInline
                  />
                </div>
              )
          }
        })()}
      </div>

      {/* Engagement section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>👍 ❤️ 42</span>
          <span>3 comments • 1 share</span>
        </div>

        <div className="flex items-center border-t border-gray-200 pt-2">
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
            </svg>
            Like
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
            </svg>
            Comment
          </button>
          <button className="flex flex-1 items-center justify-center gap-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path>
            </svg>
            Share
          </button>
        </div>
      </div>
    </div>
  )
}

const ProfileHeader = ({
  imageUrl,
  displayName,
}: {
  imageUrl: string | undefined
  displayName: string
}) => {
  return (
    <div className="flex items-center gap-3">
      <Avatar>
        {imageUrl ? (
          <div className="bg-background border-border flex h-full w-full items-center justify-center rounded-full border">
            <img src={imageUrl} />
          </div>
        ) : (
          <div className="w-full bg-gray-400"></div>
        )}
      </Avatar>
      <div className="text-left">
        <p className="text-sm font-semibold text-black">{displayName}</p>
        <p className="text-xs text-gray-500">2h • 🌐</p>
      </div>
    </div>
  )
}
